import React, { useState, useEffect, useCallback } from 'react';
import { useLazyQuery, gql, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Table, Spin, message, Input, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, DollarOutlined, UnorderedListOutlined, RobotOutlined } from '@ant-design/icons';
import AgentLiveModal from '../Agent/AgentLiveModal';

const GET_SCHEDULED_PAYMENTS_QUERY = gql`
  query GetAccountStatementsToDownload($page: Int!, $limit: Int!, $search: String) {
      accountStatementsToDownload(page: $page, limit: $limit, search: $search) {
              items {
                    id
                    user {
                        lastName
                    }
              }
              count
      }
  }
`;

const MAKE_PAYMENT_BY_CLOUD_AGENT_MUTATION = gql`
  mutation MakePaymentByCloudAgent($scheduledPaymentId: Int!) {
    makePaymentByCloudAgent(scheduledPaymentId: $scheduledPaymentId) {
      taskId
      liveUrl
      prompt
    }
  }
`;

interface AccountStatements {
  id: number;
  user: User;
}

interface User {
  lastName: string;
}

interface AccountStatementsData {
  accountStatementsToDownload: {
    items: AccountStatements[];
    count: number;
  };
}

interface AccountStatementsVars {
  limit: number;
  page: number;
  search: string;
}

const StatementsList: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [liveUrl, setLiveUrl] = useState<string>('');
  const [scheduledPayments, setScheduledPayments] = useState<AccountStatements[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [loadingPaymentId, setLoadingPaymentId] = useState<number | null>(null);
  const [agentTaskId, setAgentTaskId] = useState<string>('');
  const [agentPrompt, setAgentPrompt] = useState<string>('');
  const [agentActionType, setAgentActionType] = useState<string>('');
  const [fetchScheduledPayments] = useLazyQuery<
    AccountStatementsData,
    AccountStatementsVars
  >(GET_SCHEDULED_PAYMENTS_QUERY);
  const [makePaymentByCloudAgent, { loading: paymentLoading }] = useMutation(
    MAKE_PAYMENT_BY_CLOUD_AGENT_MUTATION
  );
  const columns: ColumnsType<AccountStatements> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Last Name",
      dataIndex: ['user', 'lastName'],
      key: "lastName",
    },
    {
      title: "Action",
      key: "action",
      fixed: 'right',
      width: 220,
      render: (_, record) => (
        <span>
          <Button
            type="default"
            onClick={() => navigate(`/scheduled-payments/${record.id}`)}
          >
            <DollarOutlined />
          </Button>
          <Button type="default">
            <UnorderedListOutlined />
          </Button>
          <Button
            type="default"
            onClick={() => handleMakePaymentByCloudAgent(record.id, 'launch')}
            loading={loadingPaymentId === record.id}
          >
            <RobotOutlined />
          </Button>
        </span>
      ),
    }
  ];

  const handleMakePaymentByCloudAgent = async (scheduledPaymentId: number, actionType: string) => {
    try {
      setLoadingPaymentId(scheduledPaymentId);

      const { data } = await makePaymentByCloudAgent({
        variables: { scheduledPaymentId },
      });

      if (data?.makePaymentByCloudAgent?.liveUrl && data?.makePaymentByCloudAgent?.taskId) {
        setLiveUrl(data.makePaymentByCloudAgent.liveUrl);
        setAgentTaskId(data.makePaymentByCloudAgent.taskId);
        setAgentPrompt(data.makePaymentByCloudAgent.prompt);
        setAgentActionType(actionType);
        setIsPreviewVisible(true);
      } else {
        message.warning('No live URL available');
      }
    } catch (error) {
      message.error('Failed to initiate payment by agent');
      console.error(error);
    } finally {
      setLoadingPaymentId(null);
    }
  };

  const loadScheduledPayments = useCallback(() => {
    setLoading(true);
    fetchScheduledPayments({
      variables: { limit: 10, page, search: debouncedSearch },
      fetchPolicy: "network-only", // Ensure a fresh query every time
      onCompleted: (data) => {
        setScheduledPayments(data.accountStatementsToDownload.items);
        setTotal(data.accountStatementsToDownload.count);
        setLoading(false);
      },
      onError: () => {
        message.error("Error loading unprocessed documents");
        setLoading(false);
      },
    });
  }, [fetchScheduledPayments, page, debouncedSearch]);

  useEffect(() => {
    loadScheduledPayments();
  }, [loadScheduledPayments]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const cleanedSearch = search.replace(/\s+/g, ''); // Remove spaces
      if (cleanedSearch.length >= 3 || cleanedSearch.length === 0) {
        setDebouncedSearch(search);
      }
    }, 300); // 300ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [search]);


  const handleTableChange = (pagination: any) => {
    setPage(pagination.current);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "start",
          alignItems: "left",
          marginBottom: 2,
        }}
      >
        <Input
          placeholder="Search"
          value={search}
          onChange={handleSearchChange}
          style={{ marginBottom: 20, width: 200, marginRight: 10 }}
          prefix={<SearchOutlined />}
        />
      </div>
      <AgentLiveModal
        visible={isPreviewVisible}
        onClose={() => setIsPreviewVisible(false)}
        taskId={agentTaskId}
        liveUrl={liveUrl}
        prompt={agentPrompt}
        actionType={agentActionType}
      />

      <Spin spinning={loading}>
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={scheduledPayments}
            pagination={{ current: page, pageSize: 50, total }}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Spin>
    </>
  );
};

export default StatementsList;
